import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { ONLY_NUMBERS_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import PercentageInput from '@/common/components/base/percentageInput/PercentageInput';
import FlowButton from '@/common/components/flowButton/FlowButton';
import useDebounce from '@/common/hooks/useDebounce';
import { getVehicleColorOption } from '@/common/queries/selections/vehicleColor/vehicleColorOption';
import { getVehicleProductYearOption } from '@/common/queries/selections/vehicleProductYear/vehicleProductYearOption';
import { InsuranceABV } from '@/common/types/SelectionOptions.type';
import { requiredText } from '@/common/utils/rules/publicRules';
import { ADJUSTER_REPORT_DAMAGED_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/damaged/damagedLinks';
import { getVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVehicleInformation/getVehicleInformationOption';
import { patchPartTwoVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchPartTwoVehicleInformation/patchPartTwoVehicleInformationOption';
import { VehicleDamagedInformationPartTwoFormType } from '@/modules/adjusterReportModules/IncidentParticipants/types/VehicleInformationType';
import { fileDetailQueryOption } from '@/modules/FileList/queries/fileDetail/fileDetailQueryOption';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Flex } from 'styled-system/jsx';

const PartTwoDamaged = () => {
  const methods = useForm<VehicleDamagedInformationPartTwoFormType>({
    mode: 'onChange',
  });

  const { id, damagedId } = useParams();
  const { data: fileDetail } = useQuery(fileDetailQueryOption({ id: +id! }));
  const navigate = useNavigate();
  const debounce = useDebounce();
  const [colorName, setColorName] = useState('');
  const { data: vehicleColors } = useQuery({
    ...getVehicleColorOption({
      insurance: fileDetail?.insurance as InsuranceABV,
      name: colorName,
    }),
    enabled: !!fileDetail?.insurance,
  });
  const { data: vehicleProductsYears } = useQuery(
    getVehicleProductYearOption(),
  );
  const { mutate: damagedPartTwoMutation, isPending } = useMutation(
    patchPartTwoVehicleInformationOption(),
  );

  const submitHandler = (data: VehicleDamagedInformationPartTwoFormType) => {
    damagedPartTwoMutation(
      {
        damaged_id: +damagedId!,
        id: +id!,
        data: {
          vehicle_manufacturing_year: data.vehicle_manufacturing_year?.label!,
          vehicle_chassis: data.vehicle_chassis!,
          vehicle_engine: data.vehicle_engine!,
          vehicle_color: data.vehicle_color?.value!,
          vehicle_vin: data.vehicle_vin!,
          fault_percentage: data.fault_percentage!,
        },
      },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };
  const { data: vehicleInformationData } = useQuery(
    getVehicleInformationOption({
      type: 'property',
      damaged_id: +damagedId!,
      id: +id!,
    }),
  );

  const vehicleTargetPart =
    vehicleInformationData?.vehicle_type?.name === 'motorcycle'
      ? 'شماره تنه'
      : 'شماره شاسی';

  useEffect(() => {
    if (vehicleInformationData) {
      methods.reset({
        vehicle_manufacturing_year:
          vehicleInformationData?.vehicle_manufacturing_year
            ? {
                label: vehicleInformationData?.vehicle_manufacturing_year,
                value: vehicleInformationData?.vehicle_manufacturing_year,
              }
            : null,
        vehicle_color: vehicleInformationData?.vehicle_color
          ? {
              label: vehicleInformationData?.vehicle_color.name,
              value: vehicleInformationData?.vehicle_color.id,
            }
          : null,
        vehicle_vin: vehicleInformationData?.vehicle_vin,
        vehicle_chassis: vehicleInformationData?.vehicle_chassis,
        vehicle_engine: vehicleInformationData?.vehicle_engine,
        fault_percentage: vehicleInformationData?.fault_percentage,
      });
    }
  }, [vehicleInformationData, methods]);

  const handleColorOnChange = (value: string) => {
    if (value) {
      debounce(() => {
        setColorName(value);
      }, 1000);
    } else {
      setColorName(value);
    }
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitHandler)}>
        <Flex direction="column" gap={1} mb="1rem">
          <ModalInput
            name="vehicle_manufacturing_year"
            label="سال ساخت"
            placeholder="انتخاب کنید"
            options={vehicleProductsYears?.map((item) => ({
              label: item.fa_const,
              value: item.const,
            }))}
            rules={{ required: { message: requiredText, value: true } }}
          />

          <ModalInput
            name="vehicle_color"
            label="رنگ وسیله‌نقلیه"
            placeholder="انتخاب کنید"
            options={vehicleColors?.map((item) => {
              return {
                label: item.name,
                value: item.id,
              };
            })}
            searchOptions={{
              onChange: handleColorOnChange,
              placeholder: 'جستجو رنگ',
            }}
            rules={{ required: { message: requiredText, value: true } }}
          />
          <TextInput
            name="vehicle_vin"
            label="شماره VIN"
            placeholder="شماره VIN را وارد کنید"
            max={17}
            rules={{
              required: { message: requiredText, value: true },
              pattern: {
                value: /^[A-Z0-9]{17}$/,
                message:
                  'شماره VIN باید 17 کاراکتر و شامل حروف بزرگ انگلیسی و یا اعداد باشد',
              },
            }}
          />
          <TextInput
            name="vehicle_chassis"
            label={vehicleTargetPart}
            placeholder={`${vehicleTargetPart} را وارد کنید`}
            rules={{ required: { message: requiredText, value: true } }}
          />
          <TextInput
            name="vehicle_engine"
            label="شماره موتور"
            placeholder="شماره موتور را وارد کنید"
            rules={{ required: { message: requiredText, value: true } }}
          />
          <PercentageInput
            percentage={10}
            label="درصد تقصیر"
            maxPercentage={100}
            name="fault_percentage"
            rules={{ required: requiredText, ...ONLY_NUMBERS_RULE }}
            placeHolder="درصد تقصیر را وارد کنید"
          />

          <FlowButton
            disabled={!methods?.formState?.isValid}
            previousStepHandler={() => {
              navigate(
                ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
                  id: id!,
                  damagedId: damagedId!,
                }).VEHICLE_INFORMATION.partOne,
                { replace: true },
              );
            }}
            loading={isPending}
            text="ثبت"
          />
        </Flex>{' '}
      </form>
    </FormProvider>
  );
};

export default PartTwoDamaged;
